import {
  deleteFetcher,
  getFetcher,
  postFetcher,
  putFetcher,
} from '@/api/fetchers';
import { DropDownList } from '@/components/DropDownList/DropDownList';

import { Paper, Stack, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { goodNotification } from '@/components/Notification/Notification';
import { FormModal } from '@/components/Modal/FormModal';
import type { Tag, Tags } from '@/schemas/API/GET/tag/response';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import {
  displayError,
  displayErrorOnUseState,
} from '@/utils/error/errorHandler';

export const LocationsPage = (): JSX.Element => {
  const { data: locations } = useSWR<Tags>('/localities', getFetcher);

  const [isNewLocationModalOpen, setNewLocationModalOpen] = useState(false);
  const [isUpdateLocationModalOpen, setUpdateLocationModalOpen] =
    useState(false);
  const [isDeleteLocationModalOpen, setIsDeleteLocationModalOpen] =
    useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null
  );
  const [nameError, setNameError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
  });

  const { data: selectedLocation } = useSWR<Tag>(
    selectedLocationId == null ? null : `/localities/${selectedLocationId}`,
    getFetcher
  ); // fetch data only if a location is selected

  const closeModal = () => {
    mutate('/localities');
    setFormData({ name: '' });
    setNameError('');
  };

  const openDeleteLocationModal = () => {
    setIsDeleteLocationModalOpen(true);
  };
  const closeDeleteLocationModal = () => {
    setIsDeleteLocationModalOpen(false);
    closeModal();
  };
  const openNewLocationModal = () => {
    setNewLocationModalOpen(true);
  };
  const closeNewLocationModal = () => {
    setNewLocationModalOpen(false);
    closeModal();
  };

  const openUpdateLocationModal = () => {
    setUpdateLocationModalOpen(true);
  };
  const closeUpdateLocationModal = () => {
    setUpdateLocationModalOpen(false);
    closeModal();
  };

  async function deleteLocation(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await deleteFetcher(`/localities/${selectedLocationId}`);
      goodNotification('Úspěch!', 'Lokace je smazana');
      closeDeleteLocationModal();
    } catch (err) {
      displayError(err);
    }
  }

  async function updateLocation(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNameError('');

    try {
      await putFetcher(`/localities/${selectedLocationId}`, formData);
      goodNotification('Lokace je upravena');
      closeUpdateLocationModal();
    } catch (err) {
      displayErrorOnUseState(err, [{ field: 'name', setState: setNameError }]);
    }
  }

  async function createNewLocation(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setNameError('');

    try {
      await postFetcher('/localities', formData);
      goodNotification('Úspěch!', 'Lokace je vytvořena');
      closeNewLocationModal();
    } catch (err) {
      displayErrorOnUseState(err, [{ field: 'name', setState: setNameError }]);
    }
  }

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData(prevState => ({ ...prevState, [name]: value })); // without this a user won't see what he/she is typing
  };

  useEffect(() => {
    if (selectedLocation !== undefined) {
      setFormData({ name: selectedLocation.name });
    }
  }, [selectedLocation]); // provide data about a selected location when it is fetched

  return (
    <Stack className="h-full p-8 overflow-auto">
      <PageHeader name="Lokace" />

      <Paper withBorder radius="md" className="p-4 flex-1 overflow-auto">
        <DropDownList
          title={'Seznam lokací'}
          list={locations?.content}
          createNewItem={openNewLocationModal}
          createNewItemButtonText="Nová lokace"
          itemToolBar={[
            {
              index: 0,
              function: (item: Tag) => {
                setSelectedLocationId(item.id);
                openUpdateLocationModal();
              },
              icon: <IconEdit />,
              onHoverColor: 'blue',
            },
            {
              index: 1,
              function: (item: Tag) => {
                openDeleteLocationModal();
                setSelectedLocationId(item.id);
              },
              icon: <IconTrash />,
            },
          ]}
        ></DropDownList>
      </Paper>
      <FormModal
        confirmButtonColor="green"
        opened={isNewLocationModalOpen}
        onClose={closeNewLocationModal}
        onConfirm={createNewLocation}
        title={'Nová lokace'}
      >
        <TextInput
          name="name"
          variant="filled"
          label="Název lokace"
          size="md"
          radius="md"
          value={formData.name}
          onChange={handleChange}
          error={nameError}
          withErrorStyles={false}
        />
      </FormModal>
      <FormModal
        opened={isUpdateLocationModalOpen}
        onClose={closeUpdateLocationModal}
        onConfirm={updateLocation}
        title={'Upavit lokace'}
      >
        <TextInput
          name="name"
          variant="filled"
          label="Název lokace"
          size="md"
          radius="md"
          error={nameError}
          onChange={handleChange}
          value={formData.name}
          withErrorStyles={false}
        />
      </FormModal>
      <FormModal
        confirmButtonColor="red"
        opened={isDeleteLocationModalOpen}
        onClose={closeDeleteLocationModal}
        onConfirm={deleteLocation}
        confirmText="Smazat"
      >
        <div className="text-lg text-center">
          Opravdu si přejete smazat tag{' '}
          <span className="font-bold italic">{selectedLocation?.name}</span> ?
        </div>
      </FormModal>
    </Stack>
  );
};

export {};
